import React from "react";
import type {DashboardIntroOption} from "./types";
import {PRE_EBIRD_LIST_BUILDING_YEAR} from "../../helpers/storedBirdData";
import {PRE_EBIRD_LIST_BUILDING_LABEL} from "./constants";

const getSpeciesLabel = (species: number) => `${species} species`;
const renderSpeciesLabel = (species: number) => <strong>{getSpeciesLabel(species)}</strong>;
const renderYearsLabel = (years: number) => <strong>{`${years} ${years === 1 ? 'year' : 'years'}`}</strong>;

export function formatDashboardYearOption(year: number) {
    return year === PRE_EBIRD_LIST_BUILDING_YEAR
        ? PRE_EBIRD_LIST_BUILDING_LABEL
        : year.toString();
}

const dashboardIntroOptions: DashboardIntroOption[][] = [
    [
        {
            heading: 'Every birder has a spark bird.',
            description: species => <>You&apos;ve already found {renderSpeciesLabel(species)}! Let&apos;s see where your adventure has taken you.</>
        },
        {
            heading: 'Your birding journey is underway!',
            description: species => <>You&apos;ve added {renderSpeciesLabel(species)} to your life list already.</>
        },
        {
            heading: 'Look at you go!',
            description: species => <>Every checklist tells a story! You&apos;ve already collected {renderSpeciesLabel(species)}.</>
        },
        {
            heading: 'Your birding journey has officially taken wing!',
            description: species => <>You&apos;ve found {renderSpeciesLabel(species)} so far.</>
        },
        {
            heading: 'The world looks different once you start noticing birds.',
            description: species => <>Here&apos;s what your first {renderSpeciesLabel(species)} have shown you so far.</>
        }
    ],
    [
        {
            heading: "You're really getting around!",
            description: species => <>Your life list is up to {renderSpeciesLabel(species)}.</>
        },
        {
            heading: "You've been keeping busy!",
            description: species => <>Over {renderSpeciesLabel(species)} have crossed your binoculars.</>
        },
        {
            heading: "You're building quite the life list!",
            description: species => <>You&apos;re up to {renderSpeciesLabel(species)} so far. Let&apos;s revisit some favorite moments.</>
        },
        {
            heading: "There's a story behind every bird.",
            description: species => <>You&apos;ve already written {renderSpeciesLabel(species)} into yours.</>
        },
        {
            heading: "You've covered some ground.",
            description: species => <>Let&apos;s see where your {renderSpeciesLabel(species)} have taken you.</>
        }
    ],
    [
        {
            heading: "Now that's a respectable life list.",
            description: species => <>{renderSpeciesLabel(species)} and counting.</>
        },
        {
            heading: "You've definitely caught the birding bug.",
            description: species => <>You&apos;re up to {renderSpeciesLabel(species)} so far. Let&apos;s explore everything you&apos;ve found.</>
        },
        {
            heading: 'Your binoculars have seen some miles.',
            description: species => <>You&apos;re up to {renderSpeciesLabel(species)} so far. Let&apos;s relive the highlights.</>
        },
        {
            heading: "You've made memories in a lot of habitats.",
            description: species => <>You&apos;re up to {renderSpeciesLabel(species)} so far. Here&apos;s where the birds have taken you.</>
        }
    ],
    [
        {
            heading: "You've got some history here.",
            description: (species, years) => <>Your life list now spans {renderSpeciesLabel(species)} over {renderYearsLabel(years)}.</>
        },
        {
            heading: "That's one impressive life list.",
            description: species => <>You&apos;re up to {renderSpeciesLabel(species)} so far. Let&apos;s take another look.</>
        },
        {
            heading: "This isn't your first migration.",
            description: species => <>You&apos;re up to {renderSpeciesLabel(species)} so far. Let&apos;s revisit your birding history.</>
        }
    ],
    [
        {
            heading: "Now that's a life list!",
            description: species => <>{renderSpeciesLabel(species)} is worth tipping a hat to.</>
        },
        {
            heading: "You've birded places most people only read about.",
            description: species => <>You&apos;ve seen {renderSpeciesLabel(species)}. Let&apos;s revisit the journey.</>
        },
        {
            heading: 'Some people collect stamps. You collect continents.',
            description: species => <>Your {renderSpeciesLabel(species)} tell a much bigger story.</>
        },
        {
            heading: "You've spent years chasing birds around the world.",
            description: species => <>Here&apos;s the story behind your {renderSpeciesLabel(species)}.</>
        },
        {
            heading: 'Every species is a memory.',
            description: species => <>Let&apos;s revisit all {renderSpeciesLabel(species)} of yours.</>
        }
    ]
];

export const getDashboardIntroTier = (species: number) => {
    if (species < 100) return dashboardIntroOptions[0];
    if (species <= 300) return dashboardIntroOptions[1];
    if (species <= 800) return dashboardIntroOptions[2];
    if (species <= 1500) return dashboardIntroOptions[3];

    return dashboardIntroOptions[4];
};
